
import { normalizeNameOfPerson } from '../normalize/normalizeNameOfPerson';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { PRIMARY_CONTACT_PERSON_TYPES } from '@constants/ssap/hhmTypes/primaryContactPersonTypes';
import { get, set } from '@utils/miscellaneous/objectHelpers';

// Types

import { ContactMethod } from '@components/ssap/SsapTypes';

const findPrimaryContactIndex = hhms => {

    const primaryContactIndex = hhms.findIndex(hhm => PRIMARY_CONTACT_PERSON_TYPES.some(pt => pt === hhm.applicantPersonType));

    if(Array.isArray(hhms) && hhms.length > 0 && !~primaryContactIndex) {
        console.table(hhms.map(hhm => {

            const {
                applicantPersonType,
                name
            } = hhm;

            return {
                name: normalizeNameOfPerson(name),
                ['applicantPersonType on watch']: PRIMARY_CONTACT_PERSON_TYPES.join(', '),
                ['applicantPersonType (actual)']: applicantPersonType
            };
        }));
    }
    return primaryContactIndex;
};

const checkIfHhmMarriedByGuid = (bloodRelationships, guid, codes) => {
    return !!bloodRelationships.find(br => br.personGuid === guid && br.relation === codes.SPOUSE);
};

const getPrimaryContact = hhms => {
    const primaryContactIndex = findPrimaryContactIndex(hhms);
    return ~primaryContactIndex ?
        hhms[primaryContactIndex] :
        {
            applicantGuid: null,
            bloodRelationship: [],
            dateOfBirth: null,
            emergencyContactInfo: {
                name: {
                    firstName: null,
                    middleName: null,
                    lastName: null,
                    suffix: null
                }
            },
            householdContact: {
                ...EMPTY_OBJECTS.HOUSEHOLD_CONTACT,
                contactPreferences: {
                    ...EMPTY_OBJECTS.HOUSEHOLD_CONTACT.contactPreferences,
                    preferredContactMethod: ContactMethod.NONE,
                    preferredWrittenLanguage: 'English',
                    preferredSpokenLanguage: 'English'
                }
            },
            name: {
                firstName: '',
                middleName: '',
                lastName: '',
                suffix: ''
            },
            flagBucket: {
                taxHouseholdCompositionFlags: 0
            }
        };
};

const getApplicantGuids = householdMembers => {
    return householdMembers.map(hhm => ({ applicantGuid: hhm.applicantGuid }));
};

const updatePrimaryContact = (hhms, newValue, isComparisonMode = false, syncOtherMembers) => {

    const primaryContactIndex = findPrimaryContactIndex(hhms);

    if(!~primaryContactIndex) {
        return hhms;
    }

    if(isComparisonMode && ~primaryContactIndex) {
        Object.keys(newValue).forEach(key => {
            console.table(hhms[primaryContactIndex][key]);
            console.table(newValue[key]);
        });
    }

    return hhms.map((hhm , idx) => {
        if(idx === primaryContactIndex) {
            return {
                ...hhm,
                ...newValue
            };
        } else if (syncOtherMembers && syncOtherMembers.length) {
            return syncOtherMembers.reduce((syncedData, syncData) => {
                const { syncKey = [], checkKey = [], checkValues = [] } = syncData;
                return set(
                    syncedData,
                    syncKey, 
                    checkValues.includes(get(hhm, checkKey)) ? get(newValue, syncKey) : get(hhm, syncKey)
                );
            }, hhm);
        }
        else {
            return hhm;
        }
    });
};

export const extractFromHouseholdMembers = householdMembers => {

    const defaultValue = {
        firstName: '...',
        middleName: '...',
        lastName: '...',
        suffix: '...'
    };

    const extractGuid = hhm => hhm ? hhm.applicantGuid : 'Guid has not been found';

    const extractName = hhm => {
        if(hhm) {
            if(hhm.name){
                return normalizeNameOfPerson(hhm.name);
            } else {
                return defaultValue;
            }
        } else {
            return defaultValue;
        }
    };

    const findHhmByProp = prop => propValue => {
        const hhm = householdMembers.find(hhm => hhm[prop] === propValue);
        if(hhm) {
            return hhm;
        } else {
            return {};
        }
    };


    const findHhmByIndex = idx => {
        const hhm = householdMembers[idx];
        if(hhm) {
            return hhm;
        } else {
            return {};
        }
    };


    const getFullNameByHhmId = id => extractName(findHhmByProp('personId')( id));
    const getFullNameByHhmGuid = guid => extractName(findHhmByProp('applicantGuid')(guid));
    const getFullNameByHhmIndexInArray = idx => extractName(findHhmByIndex(idx));

    const getGuidByPersonId = id => extractGuid(findHhmByProp('personId')(id));
    const getGuidByIndexInArray = idx => extractGuid(findHhmByIndex(idx));

    const getHhmByGuid = guid => findHhmByProp('applicantGuid')(guid);
    const getHhmByPersonId = personId => findHhmByProp('personId')(personId);
    const getHhmGenderByGuid = guid => {
        const gender = getHhmByGuid(guid).gender;
        if(gender !== undefined) {
            return gender;
        } else {
            return null;
        }
    };

    return {
        getFullNameByHhmId,
        getFullNameByHhmGuid,
        getFullNameByHhmIndexInArray,
        getGuidByPersonId,
        getGuidByIndexInArray,
        getHhmByGuid,
        getHhmByPersonId,
        getHhmGenderByGuid
    };
};

const getPrimaryContactPersonIndex = hhms => {
    const idx = findPrimaryContactIndex(hhms);
    return idx >= 0 ? idx : Infinity;
};

export const householdMemberTools = {
    getApplicantGuids,
    getPrimaryContact,
    updatePrimaryContact,
    extractFromHouseholdMembers,
    checkIfHhmMarriedByGuid,
    getPrimaryContactPersonIndex
};
